import { parseJSONSafely } from "./common";


// Kiểm tra item có phải là insurance item hay không
// Insurance types: 6 (MOTORBIKE_TNDSBB), 7 (CAR_TNDSBB)
export const isInsuranceItem = (order, item) => {
  if (!item) return false;
  
  const INSURANCE_TYPES = [6, 7];
  const orderItemOtherData = parseJSONSafely(item?.orderItemOtherData);
  const hasInsuranceInfo =
    orderItemOtherData?.insuranceDuration ||
    orderItemOtherData?.insuranceStartDate ||
    orderItemOtherData?.insuranceEndDate;

  return (
    INSURANCE_TYPES.includes(Number(order?.orderType)) ||
    INSURANCE_TYPES.includes(Number(item?.productCategory)) ||
    hasInsuranceInfo
  );
};


//Tính toán promotion info từ order
export const getPromotionInfo = (order, discountAmount = 0) => {
  const orderOtherData = parseJSONSafely(order?.orderOtherData);
  
  const promotionCode = (
    order?.promotionData?.promotionCode ??
    orderOtherData?.promotionCode ??
    orderOtherData?.discountCode ??
    orderOtherData?.couponCode
  );

  const promotionPercent = (
    order?.promotionData?.promotionPercentage ??
    orderOtherData?.promotionPercentage ??
    orderOtherData?.promotionCodePercent
  );

  const hasPromotion =
    promotionCode !== undefined && 
    promotionCode !== null && 
    promotionCode !== "";
    
  const hasPercent =
    promotionPercent !== undefined && 
    promotionPercent !== null && 
    promotionPercent !== "";

  return {
    hasData: discountAmount > 0 || hasPromotion || hasPercent,
    promotionCode: promotionCode || "-",
    promotionPercent: promotionPercent ? `${promotionPercent}%` : "-",
    discountAmount,
  };
};


export const getGiftLicensePlates = (order) => (order?.orderItems || [])
  .flatMap((orderItem) => {
    const value = orderItem?.orderItemValue;
    try {
      const parsedValue = JSON.parse(value);
      return Array.isArray(parsedValue) ? parsedValue : [parsedValue];
    } catch {
      return value ? [value] : [];
    }
  })
  .filter(Boolean)
  .join(", ");


/**
 * Chuẩn hóa biển số khi lưu/gửi API/import file.
 * - Bỏ khoảng trắng, dấu chấm, dấu gạch, ký tự đặc biệt
 * - Chuyển in hoa
 */
export function normalizePlate(raw = "") {
  return String(raw || "")
    .trim()
    .replace(/[^0-9A-ZĐa-zđ]/g, "")
    .toUpperCase();
}

/**
 * Dùng cho onChange của input biển số xe.
 * - Bỏ dấu tiếng Việt, trừ Đ/đ giữ nguyên thành Đ
 * - Bỏ khoảng trắng, ký tự đặc biệt
 * - Chuyển in hoa
 * - Không xóa mất text khi gõ Telex/VNI
 */
export function sanitizePlateInput(raw = "") {
  const str = String(raw || "");

  // Giữ lại đ/Đ trước khi normalize NFD
  const preserved = str.split("đ").join("\u0001").split("Đ").join("\u0002");

  // NFD tách dấu thành combining characters, rồi xóa combining chars
  const stripped = preserved.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

  // Khôi phục Đ
  const restored = stripped.split("\u0001").join("Đ").split("\u0002").join("Đ");

  return restored
    .replace(/\s/g, "")
    .replace(/[^0-9A-ZĐa-zđ]/g, "")
    .toUpperCase();
}

function checkSpecialSerialPlate(plateNumber) {
  const specialSerialChar = "KT,LD,DA,MK,MD,MĐ,TD,TĐ,HC,NG,QT,NN,CV,CD,LB,RM".split(",");
  const includedSpecialSerialChars = specialSerialChar.filter((char) => plateNumber.includes(char));

  if (includedSpecialSerialChars.length !== 1) {
    return false;
  }

  const specialChar = includedSpecialSerialChars[0];
  const specialSerialIndex = plateNumber.indexOf(specialChar);
  const specialSerialLength = specialChar.length;

  const beforeSpecialSerial = plateNumber.slice(0, specialSerialIndex);
  const afterSpecialSerial = plateNumber.slice(specialSerialIndex + specialSerialLength);

  return /^\d+$/.test(beforeSpecialSerial) && /^\d+$/.test(afterSpecialSerial);
}

function checkValidVehicleIdentity(vehicleIdentity) {
  let isValidVehicle = true;
  
  function checkingValidPlateNumber(plateNumber) {
    return !/[^A-Z0-9Đ]/g.test(plateNumber);
  }

  const validPlateNumber = checkingValidPlateNumber(vehicleIdentity);
  if (!validPlateNumber) {
    return false;
  }

  const specialSerialChar = "KT,LD,DA,MK,MD,MĐ,TD,TĐ,HC,NG,QT,NN,CV,CD,LB,RM".split(",");
  const normalSerialChar = ["A", "B", "C", "D", "E", "F", "G", "H", "K", "L", "M", "N", "P", "S", "T", "U", "V", "X", "Y", "Z", "R"];

  // Biển quân đội được đứng đầu chuỗi, ví dụ: TM2366
  const militaryPrefix = [
    "TM", "TC", "TH", "TT", "TK", "TN",
    "KA", "KB", "KC", "KD", "KV", "KP", "KK", "KT",
    "AA", "AB", "AC", "AD",
    "QA", "QH", "QB", "QC", "QM",
    "BL",
    "BB", "BC", "BK", "BP", "BH", "BT",
    "HA", "HB", "HC", "HE", "HD", "HH", "HT", "HQ", "HN",
    "PA", "PG", "PK", "PQ", "PM", "PX",
    "AV", "AT", "AN", "AX", "AM",
    "VT", "CA", "CB", "CD", "CH", "CK", "CM", "CN", "CP", "CT", "CV",
    "PP",
  ];

  const matchedMilitaryPrefix = militaryPrefix.find((prefix) => vehicleIdentity.startsWith(prefix));
  if (matchedMilitaryPrefix) {
    const numberPart = vehicleIdentity.slice(matchedMilitaryPrefix.length);

    if (!/^\d+$/.test(numberPart)) {
      isValidVehicle = false;
    }

    const MAX_LENGTH = 12;
    const MIN_LENGTH = 4;

    if (vehicleIdentity.length < MIN_LENGTH || vehicleIdentity.length > MAX_LENGTH) {
      isValidVehicle = false;
    }

    return isValidVehicle;
  }

  const includedNormalSerialChars = normalSerialChar.filter((char) => vehicleIdentity.includes(char));
  const includedSpecialSerialChars = specialSerialChar.filter((char) => vehicleIdentity.includes(char));
  let serialIndex = -1;
  let serialLength = 0;

  if (checkSpecialSerialPlate(vehicleIdentity)) {
    const MAX_LENGTH = 12;
    const MIN_LENGTH = 6;

    if (vehicleIdentity.length < MIN_LENGTH || vehicleIdentity.length > MAX_LENGTH) {
      return false;
    }

    return true;
  }
    
  if (includedSpecialSerialChars.length > 0) {
    if (includedSpecialSerialChars.length !== 1) {
      isValidVehicle = false;
    } else {
      const includedChar = includedSpecialSerialChars[0];
      serialIndex = vehicleIdentity.indexOf(includedChar);
      serialLength = includedChar.length;

      const isContainRedundantNormalChar = includedNormalSerialChars.some((char) => {
        const index = vehicleIdentity.indexOf(char);
        return index >= 0 && index < serialIndex;
      });

      if (isContainRedundantNormalChar) {
        isValidVehicle = false;
      }
    }
  } else if (includedNormalSerialChars.length === 1 || includedNormalSerialChars.length === 2) {
    serialIndex = vehicleIdentity.indexOf(includedNormalSerialChars[0]);
    serialLength = (vehicleIdentity.match(/[A-ZĐ]/g) || []).length;
  } else {
    isValidVehicle = false;
  }

  const totalLetterCount = (vehicleIdentity.match(/[A-ZĐ]/g) || []).length;

  // Chỉ cho phép 1 hoặc 2 chữ cái
  if (totalLetterCount > 2 || totalLetterCount === 0) {
    isValidVehicle = false;
  }

  // Nếu có 2 chữ thì phải đứng liền nhau
  if (totalLetterCount === 2) {
    const firstIndex = vehicleIdentity.search(/[A-ZĐ]/);
    const secondIndexRelative = vehicleIdentity.slice(firstIndex + 1).search(/[A-ZĐ]/);

    if (firstIndex === -1 || secondIndexRelative === -1) {
      isValidVehicle = false;
    } else {
      const secondIndex = firstIndex + 1 + secondIndexRelative;
      if (secondIndex - firstIndex !== 1) {
        isValidVehicle = false;
      }
    }
  }

  // Chữ đầu tiên phải bắt đầu sau đúng 2 ký tự đầu
  const firstLetterIndex = vehicleIdentity.search(/[A-ZĐ]/);
  if (firstLetterIndex !== 2) {
    isValidVehicle = false;
  }

  // Hai ký tự đầu phải là số
  const firstTwoChars = vehicleIdentity.slice(0, 2);
  if (!/^\d{2}$/.test(firstTwoChars)) {
    isValidVehicle = false;
  }

  // Phần sau seri phải toàn là số
  if (serialIndex >= 0) {
    const numberPart = vehicleIdentity.slice(serialIndex + serialLength);
    if (!/^\d+$/.test(numberPart)) {
      isValidVehicle = false;
    }
  }

  const MAX_LENGTH = 12;
  const MIN_LENGTH = 6;

  if (vehicleIdentity.length < MIN_LENGTH || vehicleIdentity.length > MAX_LENGTH) {
    isValidVehicle = false;
  }

  return isValidVehicle;
}

export function validatePlateNumber(plate, options = {}) {
  const raw = plate || "";
  const p = normalizePlate(raw);
  const shouldCheckRawSpecialChars = options.checkRawSpecialChars !== false;

  if (!p) return "Vui lòng nhập biển số xe.";

  if (shouldCheckRawSpecialChars && /[^0-9A-ZĐa-zđ]/.test(raw.trim())) {
    return "Biển số chỉ được chứa chữ và số, vui lòng không nhập khoảng trắng hoặc ký tự đặc biệt.";
  }

  if (!/\d/.test(p)) {
    return "Biển số xe không hợp lệ, vui lòng kiểm tra lại.";
  }

  if (!checkValidVehicleIdentity(p)) {
    return "Biển số xe không hợp lệ, vui lòng kiểm tra lại.";
  }

  return "";
}

// Giữ tên cũ để không phải sửa nhiều file đang dùng validateVehicleIdentity
export const validateVehicleIdentity = validatePlateNumber;

export const validatorPlateNumber = (value) => {
  const error = validatePlateNumber(value);

  if (error) {
    return Promise.reject(new Error(error));
  }

  return Promise.resolve();
};


export const validatePhoneNumber = (value) => {
  const phone = String(value || "").trim();
  const phonePattern = /^(03|05|07|08|09)\d{8}$/;

  if (!phone) return "Vui lòng nhập số điện thoại";
  if (!phonePattern.test(phone)) return "Số điện thoại không hợp lệ";
  return "";
};

export const validateEmail = (value) => {
  const email = String(value || "").trim();
  const emailPattern = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  if (!email) return "Vui lòng nhập email";
  if (!emailPattern.test(email)) return "Email không hợp lệ";
  return "";
};

export const validateGiftField = (validator, value) => {
  const message = validator(value);
  return message ? Promise.reject(new Error(message)) : Promise.resolve();
};